import { Drawer } from "@mui/material";
import { useWasteLineUnifiedSuspense } from "hooks/useWasteLineUnified";
import { Suspense, useState } from "react";

import { LineItemDetailDialog } from "../ManifestLineItems/LineItemDetailDialog";
import { LineItemsDataGrid } from "./LineItemsDataGrid";
import { ManifestDetailSection, ManifestDetailSectionSkeleton } from "./ManifestDetailSection";

interface ManifestDetailDrawerProps {
  /**
   * Identifies the waste line the drawer was opened for. The drawer fetches
   * the line to look up its parent manifest, then renders the full manifest
   * detail with the embedded line-items grid pre-filtered to this line.
   */
  lineItemId: string | undefined;
  open: boolean;
  onClose: () => void;
}

/**
 * Drawer opened when a deep link points at a specific waste line — e.g. from
 * the tenant Waste Streams modal or the all-line-items page. Hosts the full
 * manifest detail (so the user lands on the line's parent manifest, line
 * items table and all) with the embedded grid pre-filtered to the targeted
 * line. Clearing the filter reveals the rest of the manifest. Clicking a row
 * inside the grid opens `LineItemDetailDialog` for view/edit, mirroring the
 * manifests-page drawer's UX.
 */
export function ManifestDetailDrawer({ lineItemId, open, onClose }: ManifestDetailDrawerProps) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          // Wider than the original line-item panel — the embedded line-items
          // grid needs the room to fit Amount, Management Method, etc. without
          // truncation — but capped so the underlying waste-stream modal (1350)
          // still shows on its left edge.
          width: { xs: "100%", sm: 700, md: 900, lg: 1100, xl: 1250 },
          boxSizing: "border-box",
        },
      }}
    >
      {open && (
        <Suspense fallback={<ManifestDetailSectionSkeleton />}>
          <ManifestDetailDrawerContent lineItemId={lineItemId} onClose={onClose} />
        </Suspense>
      )}
    </Drawer>
  );
}

const ManifestDetailDrawerContent = ({
  lineItemId,
  onClose,
}: {
  lineItemId: string | undefined;
  onClose: () => void;
}) => {
  const { lineItem } = useWasteLineUnifiedSuspense(lineItemId);
  const processedManifestId = lineItem?.processedManifestId;
  // Local state so the dialog can be opened/closed independently of the
  // drawer. The parent's URL param drives which line opened the drawer; this
  // state drives which line (if any) is currently in the detail dialog on top.
  const [editingLineItemId, setEditingLineItemId] = useState<string | undefined>(undefined);
  return (
    <>
      <ManifestDetailSection processedManifestId={processedManifestId} onClose={onClose}>
        <LineItemsDataGrid
          processedManifestId={processedManifestId}
          initialWasteLineNumber={lineItem?.wasteLineNumber ?? undefined}
          onRowClick={setEditingLineItemId}
        />
      </ManifestDetailSection>
      {editingLineItemId && (
        <Suspense fallback={null}>
          <LineItemDetailDialog
            key={editingLineItemId}
            processedManifestId={processedManifestId}
            lineItemId={editingLineItemId}
            onClose={() => setEditingLineItemId(undefined)}
          />
        </Suspense>
      )}
    </>
  );
};
